import { BlogPost } from '../types';
import { parseFrontmatter } from './blogParser';

const extractFirstImageFromContent = (markdown: string): string | null => {
  const markdownImageMatch = markdown.match(/!\[[^\]]*]\(([^)\s]+)(?:\s+"[^"]*")?\)/);
  if (markdownImageMatch?.[1]) {
    return markdownImageMatch[1].trim();
  }

  const htmlImageMatch = markdown.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/i);
  if (htmlImageMatch?.[1]) {
    return htmlImageMatch[1].trim();
  }

  return null;
};

const slugify = (input: string): string =>
  input
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[\u0000-\u001F\u007F/\\?%*:|"<>#]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const normalizeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item : String(item)))
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

export const getBlogPosts = (): BlogPost[] => {
  // Use absolute path pattern relative to project root for better reliability in Vite
  // This looks for /posts/*.md starting from the project root
  const modules = import.meta.glob('/posts/*.md', { query: '?raw', import: 'default', eager: true });

  const posts = Object.keys(modules).map((path): BlogPost | null => {
    try {
        const rawContent = modules[path];
        
        // Safety check if content is not string
        if (typeof rawContent !== 'string') {
            console.warn('Blog post content is not string:', path);
            return null;
        }

        const { frontmatter, content } = parseFrontmatter(rawContent);
        
        const fileSlug = path.split('/').pop()?.replace('.md', '') || '';
        const frontmatterSlug = typeof frontmatter.slug === 'string' ? frontmatter.slug.trim() : '';
        const frontmatterTitle = typeof frontmatter.title === 'string' ? frontmatter.title.trim() : '';
        const slugSource = frontmatterSlug || frontmatterTitle || fileSlug;
        const slug = slugify(slugSource) || fileSlug;

        const imageFromFrontmatter = typeof frontmatter.image === 'string' ? frontmatter.image.trim() : '';
        const fallbackImage = imageFromFrontmatter || extractFirstImageFromContent(content) || '';

        const tags = normalizeStringArray(frontmatter.tags);
        const keywords = normalizeStringArray(frontmatter.keywords);

        return {
          slug,
          title: frontmatter.title || 'Untitled',
          excerpt: frontmatter.excerpt || '',
          // Use frontmatter description for SEO if available, otherwise fallback to excerpt
          description: frontmatter.description || frontmatter.excerpt || '',
          // Map keywords if available
          keywords: keywords.length ? keywords : tags,
          image: fallbackImage,
          date: frontmatter.date || '',
          tags,
          content: content
        };
    } catch (e) {
        console.error('Error loading blog post:', path, e);
        return null;
    }
  }).filter((post): post is BlogPost => post !== null);

  // Sort by date descending
  return posts.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return isNaN(dateB) || isNaN(dateA) ? 0 : dateB - dateA;
  });
};
